import { useEffect, useState } from 'react';
import { isDocumentVisible } from '../utils/devicePerformance';

/**
 * True when the element is in view AND the tab is visible.
 * Use to pause rAF loops, Lottie, and video decode on MacBook / Safari.
 */
export function usePausedWhenHidden(
  targetRef,
  { rootMargin = '80px 0px', enabled = true } = {}
) {
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(isDocumentVisible);
  const paused = enabled && (!inView || !pageVisible);

  useEffect(() => {
    if (!enabled) return undefined;
    const onVis = () => setPageVisible(isDocumentVisible());
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setInView(true);
      return undefined;
    }
    const node = targetRef?.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setInView(Boolean(entry?.isIntersecting)),
      { rootMargin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin, targetRef]);

  return { paused, inView, pageVisible };
}

/** Subscribe to visibility without an element ref. */
export function usePageVisible() {
  const [visible, setVisible] = useState(isDocumentVisible);
  useEffect(() => {
    const onVis = () => setVisible(isDocumentVisible());
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  return visible;
}
